import { Check, AlertTriangle, Clock } from 'lucide-react';

const BLUE = '#38BDF8';
const GREEN = '#34D399';
const AMBER = '#FBBF24';
const RED = '#E02D45';

function MockupShell({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0D0D0D] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
        </div>
        <span className="text-[11px] text-white/30 font-mono ml-2">{title}</span>
      </div>
      {children}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="px-4 py-3">
      <p className="text-[10px] text-white/25 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-lg font-black text-white/80 mt-0.5">{value}</p>
    </div>
  );
}

function StatusPill({ status, color }) {
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded-md" style={{ color, backgroundColor: `${color}15` }}>
      {status}
    </span>
  );
}

/* ═══════════════════════════════════════════
   1. SUPPLIER ONBOARDING | Approval Flow + Checklist
   ═══════════════════════════════════════════ */
export function OnboardingMockup() {
  return (
    <MockupShell title="Supplier Qualification Workflow">
      {/* Multi-Stage Approval Flow */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Approval Stages</p>
        <div className="flex gap-1">
          {['Registration', 'Plant Visit', 'Capability', 'Commercial', 'Approved'].map((s, i) => (
            <div key={i} className="flex items-center gap-1 flex-1">
              <div className={`flex-1 h-8 rounded-md flex items-center justify-center text-[8px] font-bold ${i < 3 ? 'text-white/60' : 'text-white/20'}`}
                style={{
                  backgroundColor: i < 3 ? `${BLUE}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${i < 3 ? `${BLUE}30` : 'rgba(255,255,255,0.06)'}`,
                }}>
                {s}
              </div>
              {i < 4 && <div className="text-white/10 text-[10px]">&rsaquo;</div>}
            </div>
          ))}
        </div>
      </div>
      {/* Onboarding Funnel */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Onboarding Pipeline</p>
        {[
          { stage: 'Registered', count: 8, pct: 100 },
          { stage: 'Under Qualification', count: 5, pct: 62 },
          { stage: 'Documentation', count: 4, pct: 50 },
          { stage: 'Final Approval', count: 2, pct: 25 },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <span className="text-[9px] text-white/30 w-28 truncate">{s.stage}</span>
            <div className="flex-1 h-5 bg-white/[0.02] rounded-sm overflow-hidden relative">
              <div className="h-full rounded-sm" style={{ width: `${s.pct}%`, backgroundColor: `${BLUE}${22 + i * 8}` }} />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-white/35">{s.count}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Readiness Checklist */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Readiness Checklist | PQ Castings</p>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { item: 'IATF Certificate', done: true },
            { item: 'Process FMEA', done: true },
            { item: 'Plant Layout', done: false },
            { item: 'Capability Study', done: false },
            { item: 'Commercial Terms', done: true },
            { item: 'Bank Details', done: true },
          ].map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
              <Check className="w-3 h-3 flex-shrink-0" style={{ color: d.done ? GREEN : 'rgba(255,255,255,0.12)' }} strokeWidth={2.5} />
              <span className="text-[9px] text-white/35 font-medium">{d.item}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="In Pipeline" value="19" />
        <StatBox label="Onboarded" value="184" />
        <StatBox label="Avg. Cycle" value="14 days" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   2. LINE INCIDENTS | Severity Board + Containment
   ═══════════════════════════════════════════ */
export function LineIncidentsMockup() {
  return (
    <MockupShell title="Line Incident Board">
      {/* Severity Cards */}
      <div className="grid grid-cols-3 border-b border-white/[0.06]">
        {[
          { severity: 'Critical', count: 2, c: RED },
          { severity: 'Major', count: 5, c: AMBER },
          { severity: 'Minor', count: 8, c: BLUE },
        ].map((s, i) => (
          <div key={i} className={`px-4 py-3 ${i < 2 ? 'border-r border-white/[0.06]' : ''}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.c }} />
              <p className="text-[10px] text-white/25 uppercase tracking-wider">{s.severity}</p>
            </div>
            <p className="text-2xl font-black" style={{ color: s.c, opacity: 0.7 }}>{s.count}</p>
          </div>
        ))}
      </div>
      {/* Incident Log with containment */}
      <div>
        {[
          { id: 'LI-047', desc: 'Mixed parts, Line 2 stop', supplier: 'ABC Forge', severity: 'Critical', c: RED, containment: 'Sorting', time: '2h' },
          { id: 'LI-046', desc: 'Dim OOS, lot rejection', supplier: 'PQ Castings', severity: 'Major', c: AMBER, containment: 'Quarantine', time: '5h' },
          { id: 'LI-045', desc: 'Packaging damage inward', supplier: 'Rathi Metals', severity: 'Minor', c: BLUE, containment: 'Accepted', time: '1d' },
          { id: 'LI-044', desc: 'Surface defect lot 2841', supplier: 'Sharda Motor', severity: 'Major', c: AMBER, containment: 'Sorting', time: '1d' },
        ].map((inc, i) => (
          <div key={i} className="px-4 py-3 border-b border-white/[0.04]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold font-mono" style={{ color: BLUE }}>{inc.id}</span>
                <StatusPill status={inc.severity} color={inc.c} />
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/25 font-medium">{inc.containment}</span>
              </div>
              <span className="text-[9px] text-white/15">{inc.time} ago</span>
            </div>
            <p className="text-[11px] text-white/35">{inc.desc} <span className="text-white/15">/ {inc.supplier}</span></p>
          </div>
        ))}
      </div>
      {/* Aging Bar */}
      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-white/20 uppercase tracking-wider">Incident Aging</span>
          <span className="text-[10px] text-white/25">15 open</span>
        </div>
        <div className="flex gap-1">
          {[
            { range: '< 24h', count: 6, c: GREEN },
            { range: '1-3d', count: 5, c: BLUE },
            { range: '3-7d', count: 3, c: AMBER },
            { range: '7d+', count: 1, c: RED },
          ].map(a => (
            <div key={a.range} className="flex-1 p-1.5 rounded text-center" style={{ backgroundColor: `${a.c}10`, border: `1px solid ${a.c}18` }}>
              <p className="text-[10px] font-black" style={{ color: a.c, opacity: 0.7 }}>{a.count}</p>
              <p className="text-[7px] text-white/20">{a.range}</p>
            </div>
          ))}
        </div>
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   3. DOL MANAGEMENT | OTD Gauge + Tracker
   ═══════════════════════════════════════════ */
export function DOLMockup() {
  return (
    <MockupShell title="Delivery Control Dashboard">
      {/* OTD Gauge + Stats */}
      <div className="grid grid-cols-2 border-b border-white/[0.06]">
        <div className="p-4 border-r border-white/[0.06] flex flex-col items-center">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2 self-start">On-Time Delivery</p>
          <svg viewBox="0 0 60 36" className="w-20 h-12">
            <path d="M6,32 A24,24 0 0,1 54,32" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" strokeLinecap="round" />
            <path d="M6,32 A24,24 0 0,1 54,32" fill="none" stroke={GREEN} strokeWidth="4" strokeLinecap="round" opacity="0.6"
              strokeDasharray={`${0.874 * 75.4} 75.4`} />
            <text x="30" y="30" textAnchor="middle" fill="white" fontSize="11" fontWeight="900" opacity="0.7">87%</text>
          </svg>
        </div>
        <div className="grid grid-rows-2">
          <div className="px-4 py-2 border-b border-white/[0.06] flex items-center justify-between">
            <span className="text-[10px] text-white/25">Overdue POs</span>
            <span className="text-sm font-black" style={{ color: RED, opacity: 0.7 }}>3</span>
          </div>
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-[10px] text-white/25">Shortage Alerts</span>
            <span className="text-sm font-black" style={{ color: AMBER, opacity: 0.7 }}>1</span>
          </div>
        </div>
      </div>
      {/* Commitment vs Actual Table */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Commitment vs Actual</p>
        <div className="grid grid-cols-4 gap-1 pb-1.5 mb-1 border-b border-white/[0.04]">
          {['Supplier', 'Committed', 'Actual', 'Status'].map(h => (
            <span key={h} className="text-[8px] text-white/15 uppercase tracking-wider">{h}</span>
          ))}
        </div>
        {[
          { supplier: 'ABC Forge', committed: 'Jan 15', actual: 'Jan 15', status: 'On Time', c: GREEN },
          { supplier: 'PQ Castings', committed: 'Jan 14', actual: 'Jan 16', status: '+2 days', c: AMBER },
          { supplier: 'Rathi Metals', committed: 'Jan 12', actual: 'Pending', status: 'Overdue', c: RED },
          { supplier: 'Sharda Motor', committed: 'Jan 16', actual: 'In Transit', status: 'On Track', c: BLUE },
        ].map((d, i) => (
          <div key={i} className="grid grid-cols-4 gap-1 py-1.5 border-b border-white/[0.03] items-center">
            <span className="text-[10px] text-white/40 truncate">{d.supplier}</span>
            <span className="text-[10px] text-white/25 font-mono">{d.committed}</span>
            <span className="text-[10px] text-white/25 font-mono">{d.actual}</span>
            <StatusPill status={d.status} color={d.c} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Active POs" value="47" />
        <StatBox label="Deviation" value="6" />
        <StatBox label="Avg Delay" value="1.4 days" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   4. SUPPLIER COMPLAINTS | Pareto + SLA View
   ═══════════════════════════════════════════ */
export function SupplierComplaintsMockup() {
  const pareto = [
    { issue: 'Dimension', count: 8 },
    { issue: 'Surface', count: 5 },
    { issue: 'Packaging', count: 3 },
    { issue: 'Mixed Parts', count: 2 },
    { issue: 'MTC Issue', count: 1 },
  ];
  const maxCount = 10;
  return (
    <MockupShell title="Supplier Complaint Governance">
      {/* Pareto Chart */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Complaint Pareto</p>
        <div className="flex items-end gap-2 h-16">
          {pareto.map((p, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <span className="text-[9px] text-white/30 font-bold mb-1">{p.count}</span>
              <div className="w-full rounded-t-sm" style={{
                height: `${(p.count / maxCount) * 48}px`,
                backgroundColor: i === 0 ? `${RED}45` : `${BLUE}${30 - i * 5}`,
                border: `1px solid ${i === 0 ? `${RED}40` : `${BLUE}35`}`,
                borderBottom: 'none',
              }} />
              <span className="text-[7px] text-white/20 mt-1 text-center leading-tight">{p.issue}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Response SLA View */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Response SLA</p>
        {[
          { supplier: 'ABC Forge', responded: '8h', sla: '24h', status: 'Within SLA', c: GREEN },
          { supplier: 'PQ Castings', responded: '36h', sla: '24h', status: 'SLA Breach', c: RED },
          { supplier: 'Rathi Metals', responded: 'Awaiting', sla: '24h', status: 'Pending', c: AMBER },
        ].map((s, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.03]">
            <div>
              <span className="text-[11px] text-white/40">{s.supplier}</span>
              <span className="text-[9px] text-white/15 ml-2">Response: {s.responded}</span>
            </div>
            <StatusPill status={s.status} color={s.c} />
          </div>
        ))}
      </div>
      {/* Repeat Issue Panel */}
      <div className="p-4 border-b border-white/[0.06] bg-white/[0.01]">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-white/20 uppercase tracking-wider">Repeat Issues</span>
          <span className="text-[11px] font-bold" style={{ color: RED }}>2 suppliers</span>
        </div>
        <div className="mt-1.5 flex gap-2">
          {['PQ Castings (Dim x3)', 'Rathi Metals (Surface x2)'].map((r, i) => (
            <span key={i} className="text-[9px] px-2 py-1 rounded bg-white/[0.03] text-white/30 border border-white/[0.04]">{r}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Open" value="10" />
        <StatBox label="Repeat" value="2" />
        <StatBox label="Avg Close" value="5 days" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   5. SUPPLIER AUDITS | Trend + Findings + NC/Obs
   ═══════════════════════════════════════════ */
export function SupplierAuditsMockup() {
  return (
    <MockupShell title="Supplier Audit Governance">
      {/* Score Trend Chart */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-white/20 uppercase tracking-wider">Audit Score Trend | ABC Forge</span>
          <span className="text-[10px] font-bold" style={{ color: GREEN }}>88% current</span>
        </div>
        <svg viewBox="0 0 200 40" className="w-full h-12">
          {[10,20,30].map(y => (
            <line key={y} x1="0" y1={y} x2="200" y2={y} stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
          ))}
          <polyline fill="none" stroke={BLUE} strokeWidth="1.5" opacity="0.6"
            points="10,30 40,28 70,25 100,22 130,18 160,15 190,10" />
          <polygon fill={BLUE} opacity="0.06"
            points="10,30 40,28 70,25 100,22 130,18 160,15 190,10 190,38 10,38" />
          {[10,40,70,100,130,160,190].map((x,i) => (
            <circle key={i} cx={x} cy={[30,28,25,22,18,15,10][i]} r="1.5" fill={BLUE} opacity="0.5" />
          ))}
          {['Q1','Q2','Q3','Q4','Q1','Q2','Q3'].map((d,i) => (
            <text key={i} x={10 + i * 30} y="38" textAnchor="middle" fill="rgba(255,255,255,0.12)" fontSize="5">{d}</text>
          ))}
        </svg>
      </div>
      {/* NC / Obs Summary Badges */}
      <div className="grid grid-cols-2 border-b border-white/[0.06]">
        <div className="p-3 border-r border-white/[0.06] text-center">
          <p className="text-[10px] text-white/20 uppercase tracking-wider">Non-Conformities</p>
          <p className="text-xl font-black" style={{ color: RED, opacity: 0.7 }}>6</p>
        </div>
        <div className="p-3 text-center">
          <p className="text-[10px] text-white/20 uppercase tracking-wider">Observations</p>
          <p className="text-xl font-black" style={{ color: AMBER, opacity: 0.7 }}>9</p>
        </div>
      </div>
      {/* Findings Table */}
      <div>
        {[
          { finding: 'SPC records not maintained', supplier: 'Rathi Metals', type: 'NC', c: RED },
          { finding: 'Gauge calibration overdue', supplier: 'PQ Castings', type: 'NC', c: RED },
          { finding: 'Skill matrix not updated', supplier: 'PQ Castings', type: 'Obs', c: AMBER },
          { finding: '5S deviation in stores', supplier: 'ABC Forge', type: 'Obs', c: BLUE },
        ].map((f, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <div className="flex-1 min-w-0">
              <span className="text-[11px] text-white/40 block truncate">{f.finding}</span>
              <span className="text-[9px] text-white/15">{f.supplier}</span>
            </div>
            <StatusPill status={f.type} color={f.c} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 border-t border-white/[0.06]">
        <StatBox label="Audits MTD" value="8" />
        <StatBox label="Avg Score" value="79%" />
        <StatBox label="Closure" value="72%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   6. RFQ TO LOI | Comparison Matrix + Progression
   ═══════════════════════════════════════════ */
export function RfqToLoiMockup() {
  return (
    <MockupShell title="Sourcing Intelligence | RFQ to LOI">
      {/* Sourcing Pipeline */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Pipeline Progression</p>
        <div className="flex gap-0.5">
          {[
            { stage: 'RFQ Issued', count: 4, c: BLUE, w: 28 },
            { stage: 'Quotes In', count: 3, c: BLUE, w: 22 },
            { stage: 'Evaluation', count: 2, c: AMBER, w: 18 },
            { stage: 'Negotiation', count: 2, c: AMBER, w: 16 },
            { stage: 'LOI', count: 1, c: GREEN, w: 16 },
          ].map((s, i) => (
            <div key={i} style={{ width: `${s.w}%` }}>
              <div className="h-9 rounded-md flex items-center justify-center" style={{
                backgroundColor: `${s.c}15`, border: `1px solid ${s.c}30`,
              }}>
                <span className="text-sm font-black" style={{ color: s.c, opacity: 0.7 }}>{s.count}</span>
              </div>
              <span className="text-[7px] text-white/20 block text-center mt-1 leading-tight">{s.stage}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Vendor Comparison Matrix */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Quote Comparison | Bracket Assembly</p>
        <div className="grid grid-cols-5 gap-1 pb-1.5 mb-1 border-b border-white/[0.04]">
          {['Vendor', 'Unit Cost', 'Lead', 'Quality', 'Decision'].map(h => (
            <span key={h} className="text-[7px] text-white/15 uppercase tracking-wider">{h}</span>
          ))}
        </div>
        {[
          { vendor: 'ABC Forge', price: '84.50', lead: '21d', quality: 88, rec: 'Shortlisted', c: GREEN },
          { vendor: 'PQ Castings', price: '79.20', lead: '28d', quality: 74, rec: 'Evaluate', c: AMBER },
          { vendor: 'Rathi Metals', price: '91.00', lead: '18d', quality: 65, rec: 'Declined', c: RED },
        ].map((v, i) => (
          <div key={i} className="grid grid-cols-5 gap-1 py-1.5 border-b border-white/[0.03] items-center">
            <span className="text-[10px] text-white/40 font-medium truncate">{v.vendor}</span>
            <span className="text-[10px] text-white/45 font-mono">Rs.{v.price}</span>
            <span className="text-[10px] text-white/30">{v.lead}</span>
            <span className="text-[10px] font-bold" style={{ color: v.quality >= 80 ? GREEN : v.quality >= 70 ? AMBER : RED }}>{v.quality}%</span>
            <StatusPill status={v.rec} color={v.c} />
          </div>
        ))}
      </div>
      {/* LOI Approval Progression */}
      <div className="p-4">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">LOI Approval | ABC Forge</p>
        <div className="flex gap-1">
          {['Procurement', 'Quality', 'Finance', 'Management'].map((s, i) => (
            <div key={i} className="flex-1 py-1.5 rounded-md text-center text-[8px] font-bold" style={{
              backgroundColor: i < 2 ? `${GREEN}12` : i === 2 ? `${AMBER}12` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${i < 2 ? `${GREEN}25` : i === 2 ? `${AMBER}25` : 'rgba(255,255,255,0.05)'}`,
              color: i < 2 ? GREEN : i === 2 ? AMBER : 'rgba(255,255,255,0.2)',
            }}>
              {s} {i < 2 ? '\u2713' : ''}
            </div>
          ))}
        </div>
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   7. CERTIFICATE MANAGEMENT | Status + Calendar + Table
   ═══════════════════════════════════════════ */
export function CertificateMockup() {
  return (
    <MockupShell title="Compliance Certificate Manager">
      {/* Status Summary */}
      <div className="grid grid-cols-3 border-b border-white/[0.06]">
        {[
          { label: 'Valid', value: '142', c: GREEN },
          { label: 'Expiring (30d)', value: '12', c: AMBER },
          { label: 'Expired', value: '4', c: RED },
        ].map((s, i) => (
          <div key={i} className={`px-4 py-3 ${i < 2 ? 'border-r border-white/[0.06]' : ''}`}>
            <p className="text-[10px] text-white/25 uppercase tracking-wider">{s.label}</p>
            <p className="text-xl font-black" style={{ color: s.c, opacity: 0.7 }}>{s.value}</p>
          </div>
        ))}
      </div>
      {/* Supplier-wise Compliance Table */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Supplier Compliance Record</p>
        <div className="grid grid-cols-5 gap-1 pb-1.5 mb-1 border-b border-white/[0.04]">
          {['Supplier', 'IATF', 'ISO 14001', 'NABL', 'Status'].map(h => (
            <span key={h} className="text-[7px] text-white/15 uppercase tracking-wider">{h}</span>
          ))}
        </div>
        {[
          { supplier: 'ABC Forge', iatf: true, iso: true, nabl: true, status: 'Compliant', c: GREEN },
          { supplier: 'PQ Castings', iatf: true, iso: false, nabl: true, status: 'Gap', c: AMBER },
          { supplier: 'Rathi Metals', iatf: false, iso: false, nabl: true, status: 'Non-Compliant', c: RED },
          { supplier: 'Sharda Motor', iatf: true, iso: true, nabl: false, status: 'Gap', c: AMBER },
        ].map((s, i) => (
          <div key={i} className="grid grid-cols-5 gap-1 py-1.5 border-b border-white/[0.03] items-center">
            <span className="text-[10px] text-white/40 truncate">{s.supplier}</span>
            <Check className="w-3 h-3" style={{ color: s.iatf ? GREEN : RED, opacity: 0.6 }} strokeWidth={2.5} />
            <Check className="w-3 h-3" style={{ color: s.iso ? GREEN : RED, opacity: 0.6 }} strokeWidth={2.5} />
            <Check className="w-3 h-3" style={{ color: s.nabl ? GREEN : RED, opacity: 0.6 }} strokeWidth={2.5} />
            <StatusPill status={s.status} color={s.c} />
          </div>
        ))}
      </div>
      {/* Expiry Alerts */}
      <div>
        {[
          { cert: 'IATF 16949', supplier: 'PQ Castings', days: '8 days', c: AMBER },
          { cert: 'ISO 14001', supplier: 'Rathi Metals', days: 'Expired', c: RED },
        ].map((c, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <div>
              <span className="text-[11px] text-white/40">{c.cert}</span>
              <span className="text-[9px] text-white/15 ml-2">{c.supplier}</span>
            </div>
            <StatusPill status={c.days} color={c.c} />
          </div>
        ))}
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   8. SUPPLIER DASHBOARDS | Control Tower
   ═══════════════════════════════════════════ */
export function SupplierDashboardsMockup() {
  return (
    <MockupShell title="Supplier Control Tower">
      {/* KPI Row */}
      <div className="grid grid-cols-4 border-b border-white/[0.06]">
        {[
          { label: 'Active', value: '184', trend: '+3', up: true },
          { label: 'Issues', value: '23', trend: '-5', up: true },
          { label: 'Rating', value: '78%', trend: '+2%', up: true },
          { label: 'Audits Due', value: '6', trend: '' },
        ].map((k, i) => (
          <div key={i} className={`px-3 py-3 ${i < 3 ? 'border-r border-white/[0.06]' : ''}`}>
            <p className="text-[9px] text-white/25 uppercase tracking-wider">{k.label}</p>
            <p className="text-lg font-black text-white/80">{k.value}</p>
            {k.trend && <p className="text-[9px] font-bold" style={{ color: k.up ? GREEN : RED }}>{k.trend}</p>}
          </div>
        ))}
      </div>
      {/* Category Health | Donut + Bars */}
      <div className="grid grid-cols-2 border-b border-white/[0.06]">
        <div className="p-4 border-r border-white/[0.06]">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Vendor Health</p>
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 44 44" className="w-14 h-14">
              <circle cx="22" cy="22" r="16" fill="none" stroke={GREEN} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.55 * 100.5} ${100.5}`} transform="rotate(-90 22 22)" />
              <circle cx="22" cy="22" r="16" fill="none" stroke={AMBER} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.30 * 100.5} ${100.5}`} strokeDashoffset={`${-0.55 * 100.5}`} transform="rotate(-90 22 22)" />
              <circle cx="22" cy="22" r="16" fill="none" stroke={RED} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.15 * 100.5} ${100.5}`} strokeDashoffset={`${-0.85 * 100.5}`} transform="rotate(-90 22 22)" />
            </svg>
            <div className="space-y-1">
              {[{l:'Healthy',c:GREEN,v:'55%'},{l:'Watch',c:AMBER,v:'30%'},{l:'At Risk',c:RED,v:'15%'}].map(x=>(
                <div key={x.l} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{backgroundColor:x.c,opacity:0.5}} />
                  <span className="text-[9px] text-white/25">{x.l} {x.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Score Bars */}
        <div className="p-4">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Scores</p>
          {[
            { cat: 'Quality', pct: 82, c: GREEN },
            { cat: 'Delivery', pct: 87, c: GREEN },
            { cat: 'Compliance', pct: 71, c: AMBER },
            { cat: 'Response', pct: 65, c: RED },
          ].map(h => (
            <div key={h.cat} className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[9px] text-white/25 w-16">{h.cat}</span>
              <div className="flex-1 h-2.5 bg-white/[0.03] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${h.pct}%`, backgroundColor: h.c, opacity: 0.45 }} />
              </div>
              <span className="text-[9px] font-bold w-7 text-right" style={{ color: h.c }}>{h.pct}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Pending Actions */}
      <div>
        {[
          { action: 'CAPA response overdue', supplier: 'Rathi Metals', c: RED },
          { action: 'Certificate renewal due', supplier: 'PQ Castings', c: AMBER },
          { action: 'Audit scheduled', supplier: 'ABC Forge', c: BLUE },
        ].map((a, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <div>
              <span className="text-[11px] text-white/40">{a.action}</span>
              <span className="text-[9px] text-white/15 ml-2">{a.supplier}</span>
            </div>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: a.c, opacity: 0.6 }} />
          </div>
        ))}
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   9. RATINGS & SCORECARDS | Ranking + Distribution
   ═══════════════════════════════════════════ */
export function RatingsMockup() {
  const suppliers = [
    { rank: 1, name: 'Sharda Motor', score: 91, ppm: 84, delivery: 95, cat: 'Preferred', c: GREEN },
    { rank: 2, name: 'ABC Forge Ltd', score: 88, ppm: 126, delivery: 92, cat: 'Preferred', c: GREEN },
    { rank: 3, name: 'PQ Castings', score: 74, ppm: 210, delivery: 87, cat: 'Conditional', c: AMBER },
    { rank: 4, name: 'Rathi Metals', score: 65, ppm: 480, delivery: 78, cat: 'Under Review', c: RED },
  ];
  return (
    <MockupShell title="Supplier Performance Scorecard">
      {/* Ranking Board */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Supplier Ranking</p>
        <div className="grid grid-cols-6 gap-1 pb-1.5 mb-1 border-b border-white/[0.04]">
          {['#', 'Supplier', 'Score', 'PPM', 'OTD', 'Category'].map(h => (
            <span key={h} className="text-[7px] text-white/15 uppercase tracking-wider">{h}</span>
          ))}
        </div>
        {suppliers.map((s, i) => (
          <div key={i} className="grid grid-cols-6 gap-1 py-2 border-b border-white/[0.03] items-center">
            <span className="text-[11px] font-black text-white/30">{s.rank}</span>
            <span className="text-[10px] text-white/45 font-medium truncate">{s.name}</span>
            <span className="text-[11px] font-bold" style={{ color: s.score >= 80 ? GREEN : s.score >= 70 ? AMBER : RED }}>{s.score}%</span>
            <span className="text-[10px] font-mono text-white/30">{s.ppm}</span>
            <span className="text-[10px] font-mono text-white/30">{s.delivery}%</span>
            <StatusPill status={s.cat} color={s.c} />
          </div>
        ))}
      </div>
      {/* Category Segmentation */}
      <div className="p-4">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Segmentation</p>
        <div className="flex gap-1.5">
          {[
            { cat: 'Preferred', count: 48, c: GREEN },
            { cat: 'Conditional', count: 22, c: AMBER },
            { cat: 'Under Review', count: 8, c: RED },
            { cat: 'Blocked', count: 2, c: 'rgba(255,255,255,0.2)' },
          ].map(d => (
            <div key={d.cat} className="flex-1 p-2.5 rounded-lg text-center" style={{ backgroundColor: `${d.c}10`, border: `1px solid ${d.c}18` }}>
              <p className="text-lg font-black" style={{ color: d.c, opacity: 0.7 }}>{d.count}</p>
              <p className="text-[7px] text-white/20 leading-tight mt-0.5">{d.cat}</p>
            </div>
          ))}
        </div>
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   10. CORRECTIVE ACTIONS | Pipeline + Linked Issues + Verification
   ═══════════════════════════════════════════ */
export function CorrectiveActionsMockup() {
  return (
    <MockupShell title="Supplier CAPA Governance">
      {/* Closure Pipeline */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">CAPA Closure Pipeline</p>
        <div className="flex gap-1">
          {[
            { stage: 'Root Cause', count: 4, c: RED },
            { stage: 'Action Plan', count: 3, c: AMBER },
            { stage: 'Implement', count: 5, c: BLUE },
            { stage: 'Verify', count: 2, c: BLUE },
            { stage: 'Closed', count: 18, c: GREEN },
          ].map((s, i) => (
            <div key={i} className="flex-1">
              <div className="h-10 rounded-md flex items-center justify-center" style={{
                backgroundColor: `${s.c}12`, border: `1px solid ${s.c}25`,
              }}>
                <span className="text-sm font-black" style={{ color: s.c, opacity: 0.7 }}>{s.count}</span>
              </div>
              <span className="text-[7px] text-white/20 block text-center mt-1 leading-tight">{s.stage}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Overdue Tracker */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Overdue Actions</p>
        {[
          { id: 'CAPA-089', supplier: 'Rathi Metals', days: '5d overdue', linked: 'Complaint CC-047', c: RED },
          { id: 'CAPA-085', supplier: 'PQ Castings', days: '2d overdue', linked: 'Audit NC-041', c: AMBER },
        ].map((ca, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.03]">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold font-mono" style={{ color: BLUE }}>{ca.id}</span>
                <span className="text-[10px] text-white/35">{ca.supplier}</span>
              </div>
              <span className="text-[9px] text-white/15">Linked: {ca.linked}</span>
            </div>
            <StatusPill status={ca.days} color={ca.c} />
          </div>
        ))}
      </div>
      {/* Verification Status */}
      <div className="p-4">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Effectiveness Verification</p>
        <div className="flex gap-1.5">
          {[
            { label: 'Verified OK', count: 14, c: GREEN },
            { label: 'Pending', count: 4, c: AMBER },
            { label: 'Recurred', count: 1, c: RED },
          ].map(v => (
            <div key={v.label} className="flex-1 py-2 rounded-md text-center" style={{ backgroundColor: `${v.c}10`, border: `1px solid ${v.c}18` }}>
              <p className="text-sm font-black" style={{ color: v.c, opacity: 0.7 }}>{v.count}</p>
              <p className="text-[7px] text-white/20">{v.label}</p>
            </div>
          ))}
        </div>
      </div>
    </MockupShell>
  );
}
